import React, { useMemo, useState } from 'react';
import { VOLUME_RANGE_OPTIONS } from './types';
import type { VolumeWidgetInstanceConfig, VolumeData } from './types';
import { formatVolume, buildTimeAxisFormatter, DEFAULT_COLORS } from '../../../design';
import { useDataProviderWidget } from '../../../blocks/hooks';
import { ChartWidget, ChartRangeSelector, KeyMetricBadge, sliceByRows, redundantRangeValues } from '../../../blocks/chart-widget';
import type { ChartSpec } from '../../../blocks/chart-widget';
import type { BarChartSeries, OverlaySeries, ChartLegendSeries } from '../../../blocks/charts';

// Sample data shown during dashboard construction (no asset assigned)
const SAMPLE_DATA: VolumeData = {
  ticker: 'SAMPLE',
  latest_volume: 52341200,
  adtv_3m: 61234500,
  price_history: [
    { date: '2025-01-02', volume: 41200000, adtv: 58000000 },
    { date: '2025-01-15', volume: 45100000, adtv: 58500000 },
    { date: '2025-01-31', volume: 52000000, adtv: 59000000 },
    { date: '2025-02-14', volume: 38900000, adtv: 59200000 },
    { date: '2025-02-28', volume: 47300000, adtv: 59800000 },
    { date: '2025-03-07', volume: 50200000, adtv: 60500000 },
    { date: '2025-03-14', volume: 44800000, adtv: 60800000 },
    { date: '2025-03-21', volume: 52341200, adtv: 61234500 },
  ],
};

interface VolumeWidgetRendererProps {
  config: VolumeWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: unknown) => void;
  readOnly?: boolean;
  widgetInstanceId?: string;
}

const VolumeWidgetRenderer: React.FC<VolumeWidgetRendererProps> = ({
  config,
  dashboardData = {},
  widgetInstanceId,
}) => {
  const uid = dashboardData?.assetDataUid as string | undefined;
  const isPreview = !uid;
  // Range pills trim client-side; the fetch brings everything the price tool
  // returns (no horizon param declared, so none is forwarded). Actual depth is
  // governed by the provider's fetch window — pills deeper than the returned
  // history are auto-disabled by redundantRangeValues, so they never mislabel.
  const [range, setRange] = useState<string>(config.defaultRange ?? '3M');

  const { data, loading, error, refetch } = useDataProviderWidget<VolumeData>(
    'price',
    uid,
    config as unknown as Record<string, unknown>,
    { enabled: !!uid, widgetInstanceId },
  );

  const displayData = uid ? data : SAMPLE_DATA;
  // Normalize to ascending date order: the full (untrimmed) PRICE payload arrives
  // newest-first, and trailing-row slicing + the time axis both assume ascending.
  const fullHistory = useMemo(() => {
    const rows = displayData?.price_history ?? [];
    return [...rows].sort((a, b) => a.date.localeCompare(b.date));
  }, [displayData]);

  const disabledRanges = useMemo(
    () => redundantRangeValues(VOLUME_RANGE_OPTIONS, fullHistory),
    [fullHistory],
  );

  const chartData = useMemo(() => {
    const rows = VOLUME_RANGE_OPTIONS.find((o) => o.value === range)?.rows;
    return sliceByRows(fullHistory, rows).map((row) => ({
      date: row.date,
      volume: row.volume,
      adtv: row.adtv,
    }));
  }, [fullHistory, range]);

  const series = useMemo<BarChartSeries[]>(() => [
    { yKey: 'volume', yName: 'Volume' },
  ], []);

  const overlays = useMemo<OverlaySeries[]>(() => [
    { type: 'line', yKey: 'adtv', yName: 'ADTV', markers: false },
  ], []);

  const dates = useMemo(() => chartData.map((d) => d.date as string), [chartData]);
  const timeFormatter = useMemo(() => buildTimeAxisFormatter(dates), [dates]);

  const legendSeries = useMemo<ChartLegendSeries[]>(() => {
    const lastRow = chartData.length > 0 ? chartData[chartData.length - 1] as Record<string, unknown> : null;
    return [...series, ...overlays].map((s, i) => ({
      key: s.yKey,
      name: s.yName,
      color: DEFAULT_COLORS[i % DEFAULT_COLORS.length],
      value: typeof lastRow?.[s.yKey] === 'number' ? lastRow[s.yKey] as number : null,
      formatter: formatVolume,
    }));
  }, [series, overlays, chartData]);

  const spec: ChartSpec = {
    chartKind: 'bar',
    rows: chartData,
    xKey: 'date',
    barSeries: series,
    overlays,
    showEndValueTags: true,
    endValueFormatter: formatVolume,
    xLabelFormatter: timeFormatter,
    yLabelFormatter: (params) => formatVolume(Number(params.value)),
    xTitle: 'Date',
    yTitles: { a: 'Shares' },
    legendSeries,
    footnote: 'Source: FMP',
    emptyMessage: 'No volume history available',
  };

  const header = (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-neutral-100 shrink-0">
      <span className="text-sm font-semibold text-neutral-800 mr-1">Volume</span>
      <KeyMetricBadge label="Vol" value={formatVolume(displayData?.latest_volume ?? null)} />
      <KeyMetricBadge label="ADTV 3M" value={formatVolume(displayData?.adtv_3m ?? null)} />
      <ChartRangeSelector
        className="ml-auto"
        options={VOLUME_RANGE_OPTIONS}
        value={range}
        onChange={setRange}
        disabledValues={disabledRanges}
      />
    </div>
  );

  return (
    <ChartWidget
      loading={loading}
      error={error}
      refetch={refetch}
      isPreview={isPreview}
      spec={spec}
      header={header}
    />
  );
};

export default VolumeWidgetRenderer;
